import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createTextVNode as _createTextVNode, createVNode as _createVNode, withCtx as _withCtx, resolveDynamicComponent as _resolveDynamicComponent, mergeProps as _mergeProps } from "vue"

const _hoisted_1 = { class: "appco-config-tab" }
const _hoisted_2 = {
  key: 0,
  class: "appco-main-section"
}
const _hoisted_3 = {
  key: 0,
  class: "chart-header",
  "data-testid": "appco-config-chart-header"
}
const _hoisted_4 = { class: "chart-header-icon" }
const _hoisted_5 = {
  key: 1,
  class: "chart-icon-placeholder"
}
const _hoisted_6 = { class: "chart-header-info" }
const _hoisted_7 = { class: "chart-header-title-row" }
const _hoisted_8 = { class: "chart-header-title" }
const _hoisted_9 = {
  key: 1,
  class: "appco-main-content"
}
const _hoisted_10 = { class: "col span-4" }
const _hoisted_11 = {
  key: 2,
  class: "appco-main-content"
}
const _hoisted_12 = {
  key: 3,
  class: "appco-main-content"
}
const _hoisted_13 = { class: "col span-3" }
const _hoisted_14 = { class: "content-group" }
const _hoisted_15 = {
  key: 0,
  class: "row"
}
const _hoisted_16 = { class: "col span-6" }

import { computed, ref, nextTick, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from '@shell/composables/useI18n';
import { set } from '@shell/utils/object';
import { isPrerelease } from '@shell/utils/version';
import { ZERO_TIME } from '@shell/config/types';
import { FLEET_APPCO_AUTH_GENERATE_NAME, IMAGE_PULL_SECRET_SUFFIX } from '@shell/utils/fleet-appco';
import dayjs from 'dayjs';
import LazyImage from '@shell/components/LazyImage';
import { RcIcon } from '@components/RcIcon';
import NameNsDescription from '@shell/components/form/NameNsDescription';
import AppChartCardSubHeader from '@shell/pages/c/_cluster/apps/charts/AppChartCardSubHeader';
import Labels from '@shell/components/form/Labels';
import LabeledInput from '@components/Form/LabeledInput/LabeledInput.vue';
import { RcSection } from '@components/RcSection';
import HelmOpAppCoResourcesSection from '@shell/components/fleet/HelmOpAppCoResourcesSection.vue';
import HelmOpTargetOptionsSection from '@shell/components/fleet/HelmOpTargetOptionsSection.vue';
import HelmOpValuesTab from '@shell/components/fleet/HelmOpValuesTab.vue';
import HelmOpTargetTab from '@shell/components/fleet/HelmOpTargetTab.vue';
import SelectOrCreateAuthSecret from '@shell/components/form/SelectOrCreateAuthSecret';
import AppCoVersionSelect from '@shell/components/fleet/AppCoVersionSelect.vue';
import { _VIEW, _CREATE, _EDIT } from '@shell/config/query-params';

interface ChartEntry {
  version: string;
  created?: string;
  icon?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'HelmOpAppCoConfigTab',
  props: {
    value: {},
    mode: {},
    realMode: {},
    appCoChartEntries: { default: () => ({} as Record<string, ChartEntry[]>) },
    appCoChartDeprecated: { type: Boolean, default: false },
    appCoChartsLoading: { type: Boolean, default: false },
    chartValues: { default: '' },
    chartValuesInit: { default: '' },
    yamlForm: { default: '' },
    yamlFormOptions: { default: () => [] },
    yamlDiffModeOptions: { default: () => [] },
    isYamlDiff: { type: Boolean, default: false },
    editorMode: { default: '' },
    diffMode: { default: '' },
    isRealModeEdit: { type: Boolean, default: false },
    targetsCreated: { default: '' },
    correctDriftEnabled: { type: Boolean, default: false },
    downstreamSecretsList: { default: () => [] },
    downstreamConfigMapsList: { default: () => [] },
    registerBeforeHook: {},
    hideTarget: { type: Boolean, default: false },
    hideAdvanced: { type: Boolean, default: false },
    hideChartConfig: { type: Boolean, default: false },
    nameRules: { default: () => [] }
  },
  emits: ["update:value", "update:targets", "targets-created", "update:yaml-form", "update:chart-values", "update:diff-mode", "update:auth", "update:cached-auth", "update:correct-drift", "update:downstream-resources"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

// eslint-disable-next-line func-call-spacing
const emit = __emit;

const store = useStore();
const { t } = useI18n(store);

const selectedChartName = computed(() => props.value.spec?.helm?.chart || '');

const selectedChartVersions = computed(() => {
  const chart = selectedChartName.value;

  if (!chart || !props.appCoChartEntries[chart]) {
    return [];
  }

  return props.appCoChartEntries[chart];
});

const versionOptions = computed(() => {
  return selectedChartVersions.value
    .filter((entry) => !isPrerelease(entry.version))
    .map((entry) => {
      const isZeroTime = !entry.created || entry.created === ZERO_TIME;

      return {
        label: entry.version,
        value: entry.version,
        date:  isZeroTime ? '' : dayjs(entry.created).format('MMM D, YYYY'),
      };
    })
    .sort((a, b) => b.value.localeCompare(a.value, undefined, { numeric: true, sensitivity: 'base' }));
});

const selectedVersion = computed(() => props.value.spec?.helm?.version || '');

const selectedChartEntry = computed(() => {
  const chart = selectedChartName.value;
  const version = selectedVersion.value;

  if (!chart || !props.appCoChartEntries[chart]) {
    return null;
  }

  return props.appCoChartEntries[chart].find((e) => e.version === version) || props.appCoChartEntries[chart][0] || null;
});

const chartIcon = computed(() => {
  const chart = selectedChartName.value;
  const entries = props.appCoChartEntries[chart];

  // Icon is chart-level, not version-specific — use the first (latest) entry which always carries it
  return entries?.[0]?.icon || '';
});

// Chart-level deprecated flag, resolved from the catalog chart model in the parent
const isDeprecated = computed(() => props.appCoChartDeprecated);

const chartSubHeaderItems = computed(() => {
  const items = [];
  const entry = selectedChartEntry.value;

  if (!entry) {
    return items;
  }

  if (entry.version) {
    items.push({
      icon:        'icon-version-alt',
      iconTooltip: { key: 'tableHeaders.version' },
      label:       entry.version,
    });
  }

  if (entry.created && entry.created !== ZERO_TIME) {
    items.push({
      icon:        'icon-refresh-alt',
      iconTooltip: { key: 'tableHeaders.lastUpdated' },
      label:       dayjs(entry.created).format('MMM D, YYYY'),
    });
  }

  return items;
});

const onVersionSelect = (val: string) => {
  set(props.value, 'spec.helm.version', val);
};

const appCoLockedSecrets = computed(() => {
  return props.downstreamSecretsList.filter((name) => name.startsWith(FLEET_APPCO_AUTH_GENERATE_NAME) && name.endsWith(IMAGE_PULL_SECRET_SUFFIX));
});

const isStandaloneAdvanced = computed(() => props.hideChartConfig && props.hideTarget && !props.hideAdvanced);
const isStandaloneTarget = computed(() => props.hideChartConfig && !props.hideTarget && props.hideAdvanced);

const advancedWrapperProps = computed(() => {
  if (isStandaloneAdvanced.value) {
    return {};
  }

  return {
    title:      t('fleet.helmOp.appCoConfig.advanced'),
    mode:       'with-header',
    type:       'primary',
    expandable: true,
    expanded:   false,
  };
});

const isView = computed(() => props.mode === _VIEW);
const isCreate = computed(() => props.mode === _CREATE);
const isEdit = computed(() => props.mode === _EDIT);

const valuesTabRef = ref<{ refreshYaml?:() => void } | null>(null);
const valuesExpanded = ref(true);

watch(valuesExpanded, (expanded) => {
  if (expanded) {
    refreshYamlEditor();
  }
});

const refreshYamlEditor = () => {
  nextTick(() => {
    valuesTabRef.value?.refreshYaml?.();
  });
};

__expose({ refreshYamlEditor });


return (_ctx: any,_cache: any) => {
  const _directive_clean_tooltip = _resolveDirective("clean-tooltip")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (!__props.hideChartConfig)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          (selectedChartName.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                _createElementVNode("div", _hoisted_4, [
                  (chartIcon.value)
                    ? (_openBlock(), _createBlock(_unref(LazyImage), {
                        key: 0,
                        src: chartIcon.value,
                        class: "chart-icon",
                        alt: selectedChartName.value
                      }, null, 8, ["src", "alt"]))
                    : (_openBlock(), _createElementBlock("div", _hoisted_5, [...(_cache[18] || (_cache[18] = [
                        _createElementVNode("i", {
                          class: "icon icon-helm",
                          "aria-hidden": "true"
                        }, null, -1)
                      ]))]))
                ]),
                _cache[21] || (_cache[21] = _createTextVNode()),
                _createElementVNode("div", _hoisted_6, [
                  _createElementVNode("div", _hoisted_7, [
                    _createElementVNode("h3", _hoisted_8, _toDisplayString(selectedChartName.value), 1),
                    _cache[19] || (_cache[19] = _createTextVNode()),
                    (isDeprecated.value)
                      ? _withDirectives((_openBlock(), _createBlock(_unref(RcIcon), {
                          key: 0,
                          "data-testid": "appco-config-deprecated-badge",
                          type: "alert-alt",
                          size: "medium",
                          status: "error",
                          role: "img",
                          "aria-hidden": false,
                          "aria-label": _unref(t)('generic.deprecated')
                        }, null, 8, ["aria-label"])), [
                          [_directive_clean_tooltip, _unref(t)('generic.deprecated')]
                        ])
                      : _createCommentVNode("", true)
                  ]),
                  _cache[20] || (_cache[20] = _createTextVNode()),
                  _createVNode(_unref(AppChartCardSubHeader), {
                    items: chartSubHeaderItems.value,
                    "remove-margin-bottom": true
                  }, null, 8, ["items"])
                ])
              ]))
            : _createCommentVNode("", true),
          _cache[26] || (_cache[26] = _createTextVNode()),
          (isView.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                _createVNode(_unref(SelectOrCreateAuthSecret), {
                  vertical: true,
                  value: __props.value.spec.helmSecretName,
                  namespace: __props.value.metadata.namespace,
                  "limit-to-namespace": true,
                  "delegate-create-to-parent": true,
                  "register-before-hook": __props.registerBeforeHook,
                  "in-store": "management",
                  mode: __props.mode,
                  "generate-name": _unref(FLEET_APPCO_AUTH_GENERATE_NAME),
                  "label-key": "fleet.helmOp.auth.appco",
                  "fixed-http-basic-auth": true,
                  "filter-basic-auth": _unref(FLEET_APPCO_AUTH_GENERATE_NAME),
                  "allow-none": false,
                  "cache-secrets": true,
                  "data-testid": "appco-config-auth",
                  "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => (emit('update:auth', { value: $event, key: 'helmSecretName' }))),
                  onInputauthval: _cache[1] || (_cache[1] = ($event: any) => (emit('update:cached-auth', { value: $event, key: 'helmSecretName' })))
                }, null, 8, ["value", "namespace", "register-before-hook", "mode", "generate-name", "filter-basic-auth"]),
                _cache[22] || (_cache[22] = _createTextVNode()),
                _createVNode(_unref(NameNsDescription), {
                  value: __props.value,
                  horizontal: true,
                  namespaced: false,
                  mode: __props.mode,
                  "name-label": 'fleet.helmOp.appCoConfig.name',
                  "no-bottom-margin": true,
                  "data-testid": "appco-config-name-ns-description",
                  "onUpdate:value": _cache[2] || (_cache[2] = ($event: any) => (emit('update:value', $event)))
                }, null, 8, ["value", "mode"]),
                _cache[23] || (_cache[23] = _createTextVNode()),
                _createElementVNode("div", _hoisted_10, [
                  _createVNode(AppCoVersionSelect, {
                    value: selectedVersion.value,
                    options: versionOptions.value,
                    loading: __props.appCoChartsLoading,
                    mode: __props.mode,
                    "data-testid": "appco-config-version",
                    "onUpdate:value": onVersionSelect
                  }, null, 8, ["value", "options", "loading", "mode"])
                ])
              ]))
            : (isCreate.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                  _createVNode(_unref(NameNsDescription), {
                    value: __props.value,
                    namespaced: false,
                    mode: __props.mode,
                    "name-label": 'fleet.helmOp.appCoConfig.name',
                    "no-bottom-margin": true,
                    rules: { name: __props.nameRules },
                    "data-testid": "appco-config-name-ns-description",
                    "onUpdate:value": _cache[3] || (_cache[3] = ($event: any) => (emit('update:value', $event)))
                  }, null, 8, ["value", "mode", "rules"])
                ]))
              : (isEdit.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                    _createVNode(_unref(SelectOrCreateAuthSecret), {
                      value: __props.value.spec.helmSecretName,
                      namespace: __props.value.metadata.namespace,
                      "limit-to-namespace": true,
                      "delegate-create-to-parent": true,
                      "register-before-hook": __props.registerBeforeHook,
                      "in-store": "management",
                      mode: _unref(_VIEW),
                      "generate-name": _unref(FLEET_APPCO_AUTH_GENERATE_NAME),
                      "label-key": "fleet.helmOp.auth.appco",
                      "fixed-http-basic-auth": true,
                      "filter-basic-auth": _unref(FLEET_APPCO_AUTH_GENERATE_NAME),
                      "allow-none": false,
                      "cache-secrets": true,
                      "data-testid": "appco-config-auth",
                      "onUpdate:value": _cache[4] || (_cache[4] = ($event: any) => (emit('update:auth', { value: $event, key: 'helmSecretName' }))),
                      onInputauthval: _cache[5] || (_cache[5] = ($event: any) => (emit('update:cached-auth', { value: $event, key: 'helmSecretName' })))
                    }, null, 8, ["value", "namespace", "register-before-hook", "mode", "generate-name", "filter-basic-auth"]),
                    _cache[24] || (_cache[24] = _createTextVNode()),
                    _createElementVNode("div", _hoisted_13, [
                      _createVNode(AppCoVersionSelect, {
                        value: selectedVersion.value,
                        options: versionOptions.value,
                        loading: __props.appCoChartsLoading,
                        mode: __props.mode,
                        "data-testid": "appco-config-version",
                        "onUpdate:value": onVersionSelect
                      }, null, 8, ["value", "options", "loading", "mode"])
                    ]),
                    _cache[25] || (_cache[25] = _createTextVNode()),
                    _createVNode(_unref(NameNsDescription), {
                      value: __props.value,
                      namespaced: false,
                      mode: __props.mode,
                      "name-label": 'fleet.helmOp.appCoConfig.name',
                      rules: { name: __props.nameRules },
                      "data-testid": "appco-config-name-ns-description",
                      "onUpdate:value": _cache[6] || (_cache[6] = ($event: any) => (emit('update:value', $event)))
                    }, null, 8, ["value", "mode", "rules"])
                  ]))
                : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    _cache[31] || (_cache[31] = _createTextVNode()),
    (!__props.hideTarget && isStandaloneTarget.value)
      ? (_openBlock(), _createBlock(HelmOpTargetTab, {
          key: 1,
          value: __props.value,
          mode: __props.mode,
          "real-mode": __props.realMode,
          "targets-created": __props.targetsCreated,
          "hide-additional-options": true,
          compact: true,
          "data-testid": "appco-config-target",
          "onUpdate:targets": _cache[7] || (_cache[7] = ($event: any) => (emit('update:targets', $event))),
          onTargetsCreated: _cache[8] || (_cache[8] = ($event: any) => (emit('targets-created', $event)))
        }, null, 8, ["value", "mode", "real-mode", "targets-created"]))
      : _createCommentVNode("", true),
    _cache[32] || (_cache[32] = _createTextVNode()),
    (!__props.hideTarget && !isStandaloneTarget.value)
      ? (_openBlock(), _createBlock(_unref(RcSection), {
          key: 2,
          title: _unref(t)('fleet.helmOp.target.title'),
          mode: "with-header",
          type: "primary",
          expandable: false,
          "data-testid": "appco-config-rcsection-target"
        }, {
          default: _withCtx(() => [
            _createVNode(HelmOpTargetTab, {
              value: __props.value,
              mode: __props.mode,
              "real-mode": __props.realMode,
              "targets-created": __props.targetsCreated,
              "hide-additional-options": true,
              compact: true,
              "data-testid": "appco-config-target",
              "onUpdate:targets": _cache[9] || (_cache[9] = ($event: any) => (emit('update:targets', $event))),
              onTargetsCreated: _cache[10] || (_cache[10] = ($event: any) => (emit('targets-created', $event)))
            }, null, 8, ["value", "mode", "real-mode", "targets-created"])
          ]),
          _: 1
        }, 8, ["title"]))
      : _createCommentVNode("", true),
    _cache[33] || (_cache[33] = _createTextVNode()),
    (!__props.hideAdvanced)
      ? (_openBlock(), _createBlock(_resolveDynamicComponent(isStandaloneAdvanced.value ? 'div' : _unref(RcSection)), _mergeProps({ key: 3 }, advancedWrapperProps.value, { "data-testid": "appco-config-advanced" }), {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_14, [
              (isView.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                    _createElementVNode("div", _hoisted_16, [
                      _createVNode(LabeledInput, {
                        value: __props.value.spec.helm.releaseName,
                        "onUpdate:value": _cache[11] || (_cache[11] = ($event: any) => ((__props.value.spec.helm.releaseName) = $event)),
                        mode: __props.mode,
                        "label-key": 'fleet.helmOp.source.release.fullLabel',
                        placeholder: _unref(t)('fleet.helmOp.source.release.placeholder', null, true),
                        "data-testid": "appco-config-release-name"
                      }, null, 8, ["value", "mode", "placeholder"])
                    ])
                  ]))
                : _createCommentVNode("", true),
              _cache[27] || (_cache[27] = _createTextVNode()),
              _createVNode(_unref(RcSection), {
                title: _unref(t)('fleet.helmOp.resources.label'),
                mode: "with-header",
                type: "secondary",
                background: isStandaloneAdvanced.value ? 'secondary' : undefined,
                expandable: "",
                expanded: true,
                "data-testid": "appco-config-resources"
              }, {
                default: _withCtx(() => [
                  _createVNode(HelmOpAppCoResourcesSection, {
                    value: __props.value,
                    mode: __props.mode,
                    "correct-drift-enabled": __props.correctDriftEnabled,
                    "downstream-secrets-list": __props.downstreamSecretsList,
                    "downstream-config-maps-list": __props.downstreamConfigMapsList,
                    "locked-secrets": appCoLockedSecrets.value,
                    compact: true,
                    "data-testid": "appco-config-resources-section",
                    "onUpdate:correctDrift": _cache[12] || (_cache[12] = ($event: any) => (emit('update:correct-drift', $event))),
                    "onUpdate:downstreamResources": _cache[13] || (_cache[13] = ($event: any) => (emit('update:downstream-resources', $event)))
                  }, null, 8, ["value", "mode", "correct-drift-enabled", "downstream-secrets-list", "downstream-config-maps-list", "locked-secrets"])
                ]),
                _: 1
              }, 8, ["title", "background"]),
              _cache[28] || (_cache[28] = _createTextVNode()),
              _createVNode(_unref(RcSection), {
                title: _unref(t)('fleet.helmOp.target.clusterDeploymentSettings'),
                mode: "with-header",
                type: "secondary",
                background: isStandaloneAdvanced.value ? 'secondary' : undefined,
                expandable: "",
                expanded: true,
                "data-testid": "appco-config-target-options"
              }, {
                default: _withCtx(() => [
                  _createVNode(HelmOpTargetOptionsSection, {
                    value: __props.value,
                    mode: __props.mode,
                    compact: true,
                    "data-testid": "appco-config-target-options-section"
                  }, null, 8, ["value", "mode"])
                ]),
                _: 1
              }, 8, ["title", "background"]),
              _cache[29] || (_cache[29] = _createTextVNode()),
              _createVNode(_unref(RcSection), {
                title: _unref(t)('generic.labelsAndAnnotations', {}, true),
                mode: "with-header",
                type: "secondary",
                background: isStandaloneAdvanced.value ? 'secondary' : undefined,
                expandable: "",
                expanded: true,
                "data-testid": "appco-config-labels"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(Labels), {
                    value: __props.value,
                    mode: __props.mode,
                    "display-side-by-side": false,
                    "add-icon": 'icon-plus',
                    "use-rc-button": true,
                    compact: true,
                    "data-testid": "appco-config-labels-section"
                  }, null, 8, ["value", "mode"])
                ]),
                _: 1
              }, 8, ["title", "background"]),
              _cache[30] || (_cache[30] = _createTextVNode()),
              _createVNode(_unref(RcSection), {
                expanded: valuesExpanded.value,
                "onUpdate:expanded": _cache[17] || (_cache[17] = ($event: any) => ((valuesExpanded).value = $event)),
                title: _unref(t)('fleet.helmOp.values.title'),
                mode: "with-header",
                type: "secondary",
                background: isStandaloneAdvanced.value ? 'secondary' : undefined,
                expandable: "",
                "data-testid": "appco-config-values"
              }, {
                default: _withCtx(() => [
                  _createVNode(HelmOpValuesTab, {
                    ref_key: "valuesTabRef",
                    ref: valuesTabRef,
                    value: __props.value,
                    mode: __props.mode,
                    "real-mode": __props.realMode,
                    "chart-values": __props.chartValues,
                    "chart-values-init": __props.chartValuesInit,
                    "yaml-form": __props.yamlForm,
                    "yaml-form-options": __props.yamlFormOptions,
                    "yaml-diff-mode-options": __props.yamlDiffModeOptions,
                    "is-yaml-diff": __props.isYamlDiff,
                    "editor-mode": __props.editorMode,
                    "diff-mode": __props.diffMode,
                    "is-real-mode-edit": __props.isRealModeEdit,
                    "hide-title": true,
                    "hide-banner": isStandaloneAdvanced.value,
                    "is-suse-app-collection": true,
                    "bg-border": true,
                    compact: true,
                    "data-testid": "appco-config-values-tab",
                    "onUpdate:yamlForm": _cache[14] || (_cache[14] = ($event: any) => (emit('update:yaml-form'))),
                    "onUpdate:chartValues": _cache[15] || (_cache[15] = ($event: any) => (emit('update:chart-values', $event))),
                    "onUpdate:diffMode": _cache[16] || (_cache[16] = ($event: any) => (emit('update:diff-mode', $event)))
                  }, null, 8, ["value", "mode", "real-mode", "chart-values", "chart-values-init", "yaml-form", "yaml-form-options", "yaml-diff-mode-options", "is-yaml-diff", "editor-mode", "diff-mode", "is-real-mode-edit", "hide-banner"])
                ]),
                _: 1
              }, 8, ["expanded", "title", "background"])
            ])
          ]),
          _: 1
        }, 16))
      : _createCommentVNode("", true)
  ]))
}
}

})